import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { Input } from './Input';
import { Button } from './Button';
import { CategoryPicker } from './CategoryPicker';
import { PrioritySelector } from './PrioritySelector';
import { ConfirmModal } from './ConfirmModal';
import { LimitReachedModal } from './LimitReachedModal';
import { useUser } from '../contexts/UserContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
interface CreateGoalModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function CreateGoalModal({
  isOpen,
  onClose
}: CreateGoalModalProps) {
  const navigate = useNavigate();
  const {
    addGoal,
    goals
  } = useUser();
  const {
    tier,
    limits
  } = useSubscription();
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<'Work' | 'Study' | 'Personal' | 'Health' | 'Others'>('Work');
  const [priority, setPriority] = useState<'High' | 'Medium' | 'Low'>('Medium');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Check quest limit for free users
    const activeQuests = goals.filter(g => !g.completed).length;
    if (tier === 'free' && activeQuests >= limits.maxActiveQuests) {
      setShowLimitModal(true);
      return;
    }
    // Validation
    if (!title.trim()) {
      setValidationMessage('Please enter a quest title');
      setShowValidationModal(true);
      return;
    }
    if (!dueDate) {
      setValidationMessage('Please select a due date for your quest');
      setShowValidationModal(true);
      return;
    }
    if (!dueTime) {
      setValidationMessage('Please select a due time for your quest');
      setShowValidationModal(true);
      return;
    }
    setLoading(true);
    try {
      await addGoal({
        title: title.trim(),
        category,
        priority,
        dueDate,
        dueTime
      });
      // Reset form
      setTitle('');
      setCategory('Work');
      setPriority('Medium');
      setDueDate('');
      setDueTime('');
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  return <>
      <AnimatePresence data-id="element-191">
        {isOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6" onClick={onClose} data-id="element-192">
            <motion.div initial={{
          scale: 0.95,
          y: 20
        }} animate={{
          scale: 1,
          y: 0
        }} exit={{
          scale: 0.95,
          y: 20
        }} className="bg-white dark:bg-[#252936] rounded-3xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-193">
              {/* Header */}
              <div className="flex items-center justify-between mb-6" data-id="element-194">
                <div data-id="element-195">
                  <h2 className="text-2xl font-fredoka font-bold text-game-dark dark:text-white" data-id="element-196">
                    Create New Quest
                  </h2>
                  {tier === 'free' && <p className="text-xs text-gray-500 dark:text-gray-400 font-quicksand mt-1" data-id="element-197">
                      {goals.filter(g => !g.completed).length}/
                      {limits.maxActiveQuests} active quests
                    </p>}
                </div>
                <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors" data-id="element-198">
                  <X className="w-6 h-6 text-gray-500 dark:text-gray-400" data-id="element-199" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-200">
                <Input label="Quest Title" placeholder="What do you want to accomplish?" value={title} onChange={e => setTitle(e.target.value)} required data-id="element-201" />

                <div data-id="element-202">
                  <label className="block text-sm font-quicksand font-bold text-gray-700 dark:text-gray-300 mb-3" data-id="element-203">
                    Category
                  </label>
                  <CategoryPicker selected={category} onSelect={setCategory} data-id="element-204" />
                </div>

                <div data-id="element-205">
                  <label className="block text-sm font-quicksand font-bold text-gray-700 dark:text-gray-300 mb-3" data-id="element-206">
                    Priority
                  </label>
                  <PrioritySelector selected={priority} onSelect={setPriority} data-id="element-207" />
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-208">
                  <Input label="Due Date" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} required min={new Date().toISOString().split('T')[0]} data-id="element-209" />
                  <Input label="Due Time" type="time" value={dueTime} onChange={e => setDueTime(e.target.value)} required data-id="element-210" />
                </div>

                <div className="flex gap-3 pt-4" data-id="element-211">
                  <Button type="button" variant="secondary" onClick={onClose} className="flex-1" data-id="element-212">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1" disabled={loading} data-id="element-213">
                    {loading ? 'Creating...' : 'Create Quest'}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      <ConfirmModal isOpen={showValidationModal} onClose={() => setShowValidationModal(false)} onConfirm={() => setShowValidationModal(false)} title="Missing Information" message={validationMessage} confirmText="Got it" cancelText="" variant="warning" data-id="element-214" />

      <LimitReachedModal isOpen={showLimitModal} onClose={() => setShowLimitModal(false)} onUpgrade={() => {
      setShowLimitModal(false);
      onClose();
      navigate('/upgrade');
    }} limitType="quests" data-id="element-215" />
    </>;
}